<template>
    <div class="row">
        <help-modal/>
        <div class="col-xl-12">
            <div class="ibox form">
                <div class="ibox-title ibox-grey">
                    <h4>{{ __('change_history') }}</h4>
                    <button-open-help-modal :help-name="'USB-001'"/>
                </div>
                <div class="ibox-content">
                    <div class="row">
                        <div class="col-md-2">
                            <label>{{ __('change_history_date_from') }}</label>
                            <datepicker :options="dateOptions" v-model="filters.date_from" @change="handleDateFromChange()"/>
                        </div>
                        <div class="col-md-2">
                            <label>{{ __('change_history_date_to') }}</label>
                            <datepicker :options="dateOptions" v-model="filters.date_to" @change="handleDateToChange()"/>
                        </div>
                        <div class="col-md-2">
                            <label>{{ __('change_history_subject') }}</label>
                            <select2 :options="subjectOptions" v-model="filters.subject_type" :settings="{ width: '100%' }"/>
                        </div>
                        <div class="col-md-1">
                            <label>Id</label>
                            <input type="number" min="1" class="form-control" v-model.trim="filters.subject_id">
                        </div>
                        <div class="col-md-2">
                            <label>{{ __('change_history_label') }}</label>
                            <input type="text" class="form-control" v-model.trim="filters.label">
                        </div>
                        <div class="col-md-2">
                            <label>{{ __('change_history_user') }}</label>
                            <select2 :options="userOptions" v-model="filters.user_id" :settings="{ width: '100%' }"/>
                        </div>
                        <div class="col-md-1">
                            <button class="btn btn-primary centered-button" style="margin-top: 22px" @click="fetch(1)">
                                {{ __('download') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="ibox" v-if="fetched">
                <div class="ibox-content">
                    <table-pagination-buttons
                        :meta="meta"
                        @paginate:page="fetch"
                        @paginate:per-page="changePerPage"
                    />
                    <div class="table-responsive">
                        <table class="table table-striped">
                            <thead>
                            <tr>
                                <th class="th-first">{{ __('change_history_time') }}</th>
                                <th>{{ __('change_history_object') }}</th>
                                <th>{{ __('change_history_label') }}</th>
                                <th>Id</th>
                                <th>{{ __('change_history_type') }}</th>
                                <th>{{ __('change_history_performed_by') }}</th>
                                <th class="th-last"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="!entries.length">
                                <td colspan="7" class="text-center">{{ __('no_data') }}</td>
                            </tr>
                            <tr v-for="entry in entries" :key="entry.id">
                                <td>{{ entry.created_at }}</td>
                                <td>{{ entry.subject }}</td>
                                <td>{{ entry.label || '-' }}</td>
                                <td>{{ entry.subject_id }}</td>
                                <td>{{ entry.event }}</td>
                                <td>{{ entry.performed_by }}</td>
                                <td class="text-right">
                                    <a v-if="canShow" href="#" v-tooltip="__('change_history_show_changes')" @click.prevent="show(entry)">
                                        <i class="fas fa-eye"></i>
                                    </a>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <b-modal id="change-history-preview" size="xl" scrollable :hide-footer="true">
            <template v-slot:modal-header="{ close }">
                <h3 class="w-100 text-center">{{ preview.title }}</h3>
                <button type="button" class="close" @click="close()">
                    <span aria-hidden="true">×</span>
                    <span class="sr-only">{{ __('close') }}</span>
                </button>
            </template>
            <table class="table change-history-preview">
                <thead>
                <tr>
                    <th class="th-first"></th>
                    <th v-if="preview.show_before" :class="{'th-last': !preview.show_after}">{{ __('change_history_before') }}</th>
                    <th v-if="preview.show_after" class="th-last">{{ __('change_history_after') }}</th>
                </tr>
                </thead>
                <tbody v-if="!preview.groups.length">
                <tr>
                    <td :colspan="previewColspan" class="text-center">{{ __('no_data') }}</td>
                </tr>
                </tbody>
                <tbody v-for="(group, groupIndex) in preview.groups" :key="groupIndex">
                <tr v-if="group.title" class="change-history-group">
                    <td :colspan="previewColspan" class="text-break">{{ group.title }}</td>
                </tr>
                <tr v-for="(row, index) in group.rows" :key="index">
                    <td class="text-break" :class="{'change-history-group-field': group.title}">{{ row.field }}</td>
                    <td v-if="preview.show_before" class="text-break">{{ row.before }}</td>
                    <td v-if="preview.show_after" class="text-break">{{ row.after }}</td>
                </tr>
                </tbody>
            </table>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios";
import TablePaginationButtons from "../../components/table/TablePaginationButtons";

function today() {
    const date = new Date();
    const pad = value => String(value).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}

export default {
    name: "ChangeHistory",
    components: {TablePaginationButtons},
    props: {
        subjectTypes: {
            type: Array,
            required: true
        },
        users: {
            type: Array,
            required: true
        },
        canShow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            filters: {
                date_from: today(),
                date_to: today(),
                subject_type: '',
                subject_id: '',
                label: '',
                user_id: ''
            },
            entries: [],
            meta: null,
            perPage: 25,
            fetched: false,
            preview: {
                title: '',
                show_before: true,
                show_after: true,
                groups: []
            }
        };
    },
    computed: {
        subjectOptions() {
            return [{id: '', text: '-'}].concat(this.subjectTypes);
        },
        previewColspan() {
            return 1 + this.preview.show_before + this.preview.show_after;
        },
        userOptions() {
            return [{id: '', text: '-'}].concat(this.users);
        },
        dateOptions() {
            return {
                todayBtn: "linked",
                format: "yyyy-mm-dd",
                todayHighlight: true
            };
        }
    },
    methods: {
        handleDateFromChange() {
            if (this.filters.date_to && this.filters.date_from > this.filters.date_to) {
                this.filters.date_to = this.filters.date_from;
            }
        },
        handleDateToChange() {
            if (this.filters.date_from && this.filters.date_to < this.filters.date_from) {
                this.filters.date_from = this.filters.date_to;
            }
        },
        changePerPage(perPage) {
            // opcja "wszystkie" - największy dostępny limit
            this.perPage = isNaN(parseInt(perPage)) ? 1000 : parseInt(perPage);
            this.fetch(1);
        },
        fetch(page) {
            toggleLoader();
            axios.post('/admin/change-history/get', Object.assign({}, this.filters, {
                page: page,
                per_page: this.perPage
            })).then(response => {
                this.entries = response.data.data;
                // TablePaginationButtons porównuje per_page z opcjami jako tekst
                this.meta = Object.assign({}, response.data.meta, {per_page: String(response.data.meta.per_page)});
                this.fetched = true;
                toggleLoader(false);
            }).catch(error => {
                toggleLoader(false);
                const errors = error.response && error.response.data && error.response.data.errors;
                swal({
                    title: "",
                    text: errors ? Object.values(errors)[0][0] : this.__('an_unexpected_error_has_occurred'),
                    type: "warning",
                    confirmButtonColor: "#DD6B55",
                    confirmButtonText: "Ok",
                    closeOnConfirm: true
                });
            });
        },
        show(entry) {
            toggleLoader();
            axios.post('/admin/change-history/' + entry.id + '/show').then(response => {
                this.preview = response.data;
                toggleLoader(false);
                this.$bvModal.show('change-history-preview');
            }).catch(() => {
                toggleLoader(false);
                toastr.error(this.__('an_unexpected_error_has_occurred'));
            });
        }
    }
}
</script>

<style scoped>
.change-history-preview .change-history-group td {
    background: #f3f3f4;
    font-weight: 600;
    border-top: 2px solid #d9dde0;
}
.change-history-preview .change-history-group-field {
    padding-left: 24px;
}
</style>
